import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

type RevealKind = 'up' | 'blur' | 'stagger' | 'draw';

interface RevealProps {
  children: ReactNode;
  kind?: RevealKind;
  delay?: number;
  className?: string;
  style?: CSSProperties;
  // Play on mount instead of waiting to scroll into view (above-the-fold content).
  immediate?: boolean;
  as?: 'div' | 'span' | 'h1' | 'h2' | 'p' | 'footer' | 'section';
  id?: string;
}

const kindClass: Record<RevealKind, string> = {
  up: 'mk-reveal',
  blur: 'mk-blur',
  stagger: 'mk-stagger',
  draw: 'mk-draw',
};

// Marketing-page scroll reveal. Same IntersectionObserver approach as AnimateIn, but with the
// larger, springier motion from the marketing design (see .mk-* in index.css).
export function Reveal({ children, kind = 'up', delay = 0, className = '', style, immediate, id, as: Tag = 'div' }: RevealProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (immediate) {
      requestAnimationFrame(() => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          io.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [immediate]);

  return (
    <Tag
      ref={ref as never}
      id={id}
      className={`${kindClass[kind]} ${className}`}
      style={{ ...style, ['--delay' as string]: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}
